import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import {
  isMultiLocale,
  LOCALE_META,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "../../../translations/config";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * LanguageSwitcher
 *
 * Returns null when there is only one locale (single-language mode).
 * When multi-locale is enabled, shows a dropdown with flag + language code
 * that navigates to the locale-prefixed URL on selection.
 */
export function LanguageSwitcher({ tone = "default" }: { tone?: "default" | "light" }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  // Self-hide when single locale
  if (!isMultiLocale) {
    return null;
  }

  const currentLocale = (i18n.language || "en") as SupportedLocale;
  const currentMeta = LOCALE_META[currentLocale] ?? LOCALE_META.en!;

  const handleLocaleChange = (locale: SupportedLocale) => {
    i18n.changeLanguage(locale);

    // Navigate to locale-prefixed URL
    const currentPath = window.location.pathname;
    // Strip existing locale prefix if present
    const localePattern = new RegExp(`^/(${SUPPORTED_LOCALES.join("|")})`);
    const pathWithoutLocale = currentPath.replace(localePattern, "") || "/";
    navigate({ to: `/${locale}${pathWithoutLocale}` as any });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium",
          tone === "light"
            ? "text-[#d5deea] hover:bg-white/10 hover:text-white"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
          "transition-colors duration-200",
          "outline-none",
        )}
      >
        <span>{currentMeta.flag}</span>
        <span>{currentLocale.toUpperCase()}</span>
        <span className="text-xs">&#9662;</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SUPPORTED_LOCALES.map((locale) => {
          const meta = LOCALE_META[locale];
          if (!meta) return null;
          return (
            <DropdownMenuItem
              key={locale}
              onClick={() => handleLocaleChange(locale)}
              className={cn(locale === currentLocale && "bg-muted")}
            >
              <span className="mr-2">{meta.flag}</span>
              <span>{meta.name}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
